import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Crown, Sparkles, CheckCircle2, Loader2, ExternalLink, Tag } from "lucide-react";
import BillingInfoModal from "@/components/BillingInfoModal";

export default function Plans() {
  const [billing, setBilling] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [checkoutPlan, setCheckoutPlan] = useState(null);

  const loadBilling = async () => {
    setLoadingPlans(true);
    try {
      const r = await api.get("/billing/plans");
      setBilling(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPlans(false);
    }
  };
  useEffect(() => { loadBilling(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("stripe_session_id");
    const cancelled = params.get("stripe_cancelled");
    if (cancelled) {
      toast.error("Checkout annullato");
      window.history.replaceState({}, "", "/piani");
      return;
    }
    if (!sid) return;
    let cancel = false;
    let attempts = 0;
    const poll = async () => {
      if (cancel) return;
      try {
        const r = await api.get(`/billing/checkout-status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success("Abbonamento attivato!");
          window.history.replaceState({}, "", "/piani");
          await loadBilling();
          return;
        }
        if (r.data.status === "expired") {
          toast.error("Sessione scaduta");
          window.history.replaceState({}, "", "/piani");
          return;
        }
        if (attempts < 10) { attempts++; setTimeout(poll, 2000); }
      } catch { /* ignore */ }
    };
    toast.loading("Verifica pagamento in corso...", { id: "poll" });
    poll().finally(() => toast.dismiss("poll"));
    return () => { cancel = true; };
  }, []);

  const [billingInfoModalPlan, setBillingInfoModalPlan] = useState(null); // planKey if modal open

  const startCheckout = async (planKey) => {
    setCheckoutPlan(planKey);
    try {
      const r = await api.post("/billing/checkout", { plan: planKey, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // 412 → dati di fatturazione mancanti → apri modal
      if (e?.response?.status === 412 || detail?.code === "billing_info_required") {
        setCheckoutPlan(null);
        setBillingInfoModalPlan(planKey);
        return;
      }
      toast.error(typeof detail === "string" ? detail : detail?.message || "Errore creazione checkout");
      setCheckoutPlan(null);
    }
  };

  const onBillingInfoSaved = () => {
    const plan = billingInfoModalPlan;
    setBillingInfoModalPlan(null);
    if (plan) startCheckout(plan);
  };

  const cancelSub = async () => {
    if (!window.confirm("Annullare l'abbonamento? Resterà attivo fino alla fine del periodo corrente.")) return;
    try { await api.post("/billing/cancel"); toast.success("Abbonamento programmato per cancellazione"); await loadBilling(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const resumeSub = async () => {
    try { await api.post("/billing/resume"); toast.success("Cancellazione annullata"); await loadBilling(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const status = billing?.status;
  const currentPlan = status?.plan || "free";
  const promo = billing?.active_promo;

  return (
    <div className="space-y-8" data-testid="plans-page">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold font-ui">Abbonamenti</p>
        <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] tracking-tight mt-2">Piani disponibili</h1>
        <p className="text-slate-500 mt-2 max-w-2xl">Scegli il piano che meglio si adatta alla tua attività. Tutti i pagamenti sono gestiti da Stripe e cancellabili in qualsiasi momento.</p>
      </div>

      {/* Active promo banner */}
      {promo && (
        <div className="rounded-xl p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 flex items-center gap-3" data-testid="active-promo-banner">
          <div className="w-10 h-10 rounded-full bg-purple-200 grid place-items-center shrink-0">
            <Tag className="w-5 h-5 text-purple-700" />
          </div>
          <div className="flex-1">
            <p className="font-ui font-semibold text-purple-900">{promo.label}</p>
            <p className="text-sm text-purple-700">
              {promo.discount_type === "percentage" && <>Sconto -{promo.discount_value}%</>}
              {promo.discount_type === "fixed" && <>Sconto -€{promo.discount_value}</>}
              {promo.discount_type === "free_credits" && <>+{promo.discount_value} crediti gratis</>}
              {promo.code && <> · Codice: <code className="bg-white px-1.5 py-0.5 rounded font-bold ml-1">{promo.code}</code></>}
              {promo.description && <> · {promo.description}</>}
            </p>
          </div>
        </div>
      )}

      {loadingPlans ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento piani...</div>
      ) : (
        <>
          {/* Status banner */}
          <div className={`p-5 rounded-xl border-2 ${
            currentPlan === "premium" ? "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300" :
            currentPlan === "base" ? "bg-[#EFF6FF] border-[#2563EB]/30" :
            "bg-slate-50 border-slate-200"
          }`} data-testid="current-plan-banner">
            <div className="flex justify-between items-start flex-wrap gap-3">
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Il tuo piano attuale</p>
                <p className="font-display text-3xl text-[#0F2741] mt-1 capitalize">
                  {currentPlan === "free" ? "Trial gratuito" : currentPlan}
                  {status?.source === "whitelist" && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-200 text-amber-800 align-middle">VIP</span>}
                </p>
                {currentPlan === "free" && (
                  <p className="text-sm text-slate-600 mt-1">
                    Hai usato <strong>{status.events_used}</strong> {status.events_used === 1 ? "evento" : "eventi"} su <strong>{status.free_quota}</strong> gratuiti.
                    {status.events_used >= status.free_quota && <span className="text-rose-600 font-semibold"> Quota esaurita.</span>}
                  </p>
                )}
                {currentPlan !== "free" && status?.current_period_end && (
                  <p className="text-sm text-slate-600 mt-1">
                    {status.cancel_at_period_end ? "Cessazione il" : "Si rinnova il"} <strong>{new Date(status.current_period_end).toLocaleDateString("it-IT")}</strong>
                  </p>
                )}
              </div>
              {status?.source === "stripe" && (
                <div className="flex gap-2">
                  {status.cancel_at_period_end ? (
                    <button onClick={resumeSub} className="btn-ghost border border-slate-200 text-sm">Ripristina</button>
                  ) : (
                    <button onClick={cancelSub} data-testid="btn-cancel-sub" className="btn-ghost border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm">Annulla abbonamento</button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Plan cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(billing?.plans || []).map((p) => {
              const isCurrent = currentPlan === p.key && status?.source !== "trial";
              const isPremium = p.key === "premium";
              const isCreditPack = p.category === "credit_pack";
              return (
                <div
                  key={p.key}
                  data-testid={`plan-card-${p.key}`}
                  className={`p-6 rounded-xl border-2 relative ${
                    isPremium ? "border-amber-400 bg-gradient-to-br from-white to-amber-50" :
                    isCreditPack ? "border-purple-300 bg-gradient-to-br from-white to-purple-50" :
                    "border-slate-200 bg-white"
                  } ${isCurrent ? "ring-2 ring-emerald-400" : ""}`}
                >
                  {isPremium && (
                    <span className="absolute -top-3 right-4 text-xs px-3 py-0.5 rounded-full bg-amber-400 text-amber-900 font-bold uppercase tracking-wider">Consigliato</span>
                  )}
                  {isCreditPack && (
                    <span className="absolute -top-3 right-4 text-xs px-3 py-0.5 rounded-full bg-purple-500 text-white font-bold uppercase tracking-wider">Crediti AI</span>
                  )}
                  <div className="flex items-center gap-2">
                    {isPremium ? <Crown className="w-5 h-5 text-amber-500" /> :
                     isCreditPack ? <Sparkles className="w-5 h-5 text-purple-500" /> :
                     <Sparkles className="w-5 h-5 text-[#2563EB]" />}
                    <h3 className="font-display text-2xl text-[#0F2741]">{p.name}</h3>
                  </div>
                  {p.original_price_eur && p.original_price_eur > p.price_eur ? (
                    <div className="mt-3" data-testid={`price-discounted-${p.key}`}>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-display text-4xl text-rose-600">€{Number(p.price_eur).toFixed(2).replace(".", ",")}</span>
                        <span className="text-lg text-slate-400 line-through font-display">€{Number(p.original_price_eur).toFixed(2).replace(".", ",")}</span>
                        <span className="text-slate-500 text-sm">{p.billing === "one_time" ? "una tantum" : p.billing === "yearly" ? "/anno" : "/mese"}</span>
                      </div>
                      {p.promo && (
                        <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                          <Tag className="w-3 h-3" />
                          {p.promo.discount_type === "percentage" ? `-${p.promo.discount_value}%` : `-€${p.promo.discount_value}`} {p.promo.label}
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="mt-3" data-testid={`price-${p.key}`}>
                      <span className="font-display text-4xl text-[#0F2741]">€{Number(p.price_eur).toFixed(2).replace(".", ",")}</span>
                      <span className="text-slate-500 text-sm">{p.billing === "one_time" ? " una tantum" : p.billing === "yearly" ? "/anno" : "/mese"}</span>
                    </p>
                  )}
                  <p className="text-sm text-slate-500 mt-2">{p.description}</p>
                  <ul className="mt-4 space-y-1.5">
                    {(p.features || []).map((f, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => startCheckout(p.key)}
                    disabled={isCurrent || checkoutPlan === p.key}
                    data-testid={`btn-subscribe-${p.key}`}
                    className={`mt-5 w-full ${
                      isPremium ? "btn-primary bg-amber-500 hover:bg-amber-600 border-amber-500" :
                      isCreditPack ? "btn-primary bg-purple-600 hover:bg-purple-700 border-purple-600" :
                      "btn-primary"
                    } ${isCurrent ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    {checkoutPlan === p.key ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Reindirizzamento...</>
                    ) : isCurrent ? (
                      <>Piano attivo</>
                    ) : isCreditPack ? (
                      <><Sparkles className="w-4 h-4" /> Acquista</>
                    ) : (
                      <><ExternalLink className="w-4 h-4" /> Sottoscrivi {p.name}</>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 text-center mt-2">Pagamenti sicuri via Stripe · IVA inclusa · Cancellabile in qualsiasi momento</p>
        </>
      )}

      {billingInfoModalPlan && (
        <BillingInfoModal
          planKey={billingInfoModalPlan}
          onClose={() => setBillingInfoModalPlan(null)}
          onSaved={onBillingInfoSaved}
        />
      )}
    </div>
  );
}
